// A wave's front, shaped like a gust of wind. The 2026 version's x20 sweep blew the page away
// with a drifting vortex ring. The front borrows its timing, its drift, and its ragged edge,
// scaled up to cross the whole page:
//
//   origin ●───► center c(u) = origin + drift · R(u) · (cos θd, sin θd)    θd: down ± 30°
//                reach(θ) = R(u) · (1 + g(u) · edge(θ)),   0 ≤ edge(θ) < 0.11
//                R(u) = progress(u) · full
//
// Near the front, the reach grows faster than the center drifts away, so once the front reaches
// a point, it stays reached. The words, the accents, and the bio count on that.

import type { Box } from "../render/vec.js";

/** A point or an offset, in pixels. */
export interface GustPoint {
  readonly x: number;
  readonly y: number;
}

/** The gust's tuned values. Times are in the vortex simulation's seconds, and a wave's life
    covers `sim` of them. */
export const GUST = Object.freeze({
  sim: 3,
  // The vortex's strength: it switches on within `snap`, fades over `fade`, and fades faster once
  // its ring lifts away at `liftAwayAt`, over `liftAwayTail` (0.7 in the old sweep).
  snap: 0.06,
  fade: 3,
  liftAwayAt: 1.5,
  liftAwayTail: 1.4,
  // The speed surges and lulls by up to this fraction, about `surgesPerLife` times a life.
  surge: 0.4,
  surgesPerLife: 6,
  // The center drifts by this fraction of the mean radius, within `driftSpread` of straight down.
  drift: 0.4,
  driftSpread: Math.PI / 6,
  // The ragged edge shows up at the impact and grows in with this time constant.
  impact: 0.2,
  edgeGrowth: 0.5,
  // The edge's largest extra reach, as fractions of the mean radius.
  lobes: 0.06,
  roughness: 0.05,
});

/** How many times each roughness harmonic turns around the circle. */
export const GUST_ROUGH_HARMONICS = [2, 3, 4, 7, 9, 11] as const;

/** No band reaches past this many band thicknesses from the front (the longest cuts off at 4,
    and the face's band is twice as thick), so offsets stop here. */
export const GUST_Z_LIMIT = 16;

/** The band's thickness at the wave's origin, in pixels. */
export const RIPPLE_THICK0 = 12;
/** How much the band thickens per pixel the front travels. The gust widens as it goes. */
export const RIPPLE_THICKGROW = 0.024;
/** Where the band cuts off, in thicknesses: past this the effect is too small to matter, and
    everything that reads it stops adding up. It's lopsided to fit the gust: a short reach
    ahead of the front and a long one behind it. */
export const RIPPLE_BAND_LEAD = 3;
export const RIPPLE_BAND_TRAIL = 4;

/** The band's thickness, in page pixels, when the front is at `radius`. */
export function rippleBandThickness(radius: number): number {
  return RIPPLE_THICK0 + radius * RIPPLE_THICKGROW;
}

const FULL_TURN = 2 * Math.PI;
const SURGES = 3;
const PROGRESS_STEPS = 256;
const PROGRESS_SUBSTEPS = 8;
const ROUGH_SAMPLES = 720;
// The roughness's range comes from samples, so it's widened by this fraction on each side to
// cover the true minimum and maximum. That way the edge never dips below the mean.
const ROUGH_MARGIN = 0.01;
// The edge's largest extra reach, as a fraction, at full growth.
const EDGE_LIMIT = GUST.lobes + GUST.roughness;

/** One gust's random numbers, rolled when its wave starts. The same draw gives the same gust. */
export interface GustDraw {
  /** The center's drift direction on the page, in radians: 0 is right, π/2 is down. */
  readonly driftAngle: number;
  readonly lobeCount: number;
  readonly lobePhase: number;
  /** One per entry in `GUST_ROUGH_HARMONICS`. */
  readonly roughAmplitudes: readonly number[];
  readonly roughPhases: readonly number[];
  /** The roughness's minimum and range, widened by `ROUGH_MARGIN`. */
  readonly roughMin: number;
  readonly roughSpan: number;
  /** The progress (0 to 1) at `PROGRESS_STEPS + 1` even steps through the life. */
  readonly progress: Float32Array;
}

/** A wave's front at one moment: its center, mean radius, and band thickness (in pixels), and
    how much of its edge shows. The fields change in place, so each frame can refill the same
    fronts instead of allocating new ones. */
export interface GustFront {
  x: number;
  y: number;
  radius: number;
  thickness: number;
  growth: number;
}

/** A front with the draw that shapes its edge. */
export interface GustWave extends GustFront {
  draw: GustDraw;
}

/** The vortex's strength at simulation time `tau`, before the surges. */
export function gustStrength(tau: number): number {
  const strength = (1 - Math.exp(-tau / GUST.snap)) * Math.exp(-tau / GUST.fade);
  return tau > GUST.liftAwayAt
    ? strength * Math.exp(-(tau - GUST.liftAwayAt) / GUST.liftAwayTail)
    : strength;
}

function roughness(
  amplitudes: readonly number[],
  phases: readonly number[],
  theta: number,
): number {
  let sum = 0;
  for (let k = 0; k < GUST_ROUGH_HARMONICS.length; k++)
    sum += amplitudes[k]! * Math.sin(GUST_ROUGH_HARMONICS[k]! * theta + phases[k]!);
  return sum;
}

// The front's speed at `u`, the fraction of its life gone by. The surges never outweigh the
// strength itself, so the speed never drops below 0.
function speed(u: number, rates: readonly number[], phases: readonly number[]): number {
  let surge = 0;
  for (let j = 0; j < SURGES; j++)
    surge += Math.sin(FULL_TURN * GUST.surgesPerLife * rates[j]! * u + phases[j]!) / SURGES;
  return gustStrength(u * GUST.sim) * (1 + GUST.surge * surge);
}

// The speed's integral over the life at `PROGRESS_STEPS + 1` steps, as a fraction of the total.
function progressTable(rates: readonly number[], phases: readonly number[]): Float32Array {
  const sums = new Float64Array(PROGRESS_STEPS + 1);
  const steps = PROGRESS_STEPS * PROGRESS_SUBSTEPS;
  let sum = 0;
  let before = speed(0, rates, phases);
  for (let s = 1; s <= steps; s++) {
    const now = speed(s / steps, rates, phases);
    sum += (before + now) / 2;
    before = now;
    if (s % PROGRESS_SUBSTEPS === 0) sums[s / PROGRESS_SUBSTEPS] = sum;
  }
  return Float32Array.from(sums, (value) => value / sum);
}

/** Rolls a new gust from `random` (the engine's random source). */
export function drawGust(random: () => number): GustDraw {
  const driftAngle = Math.PI / 2 + (random() * 2 - 1) * GUST.driftSpread;
  const lobeCount = 5 + Math.min(2, Math.floor(random() * 3));
  const lobePhase = random() * FULL_TURN;
  const roughAmplitudes = GUST_ROUGH_HARMONICS.map(() => 0.4 + random() * 0.6);
  const roughPhases = GUST_ROUGH_HARMONICS.map(() => random() * FULL_TURN);
  const rates = Array.from({ length: SURGES }, () => 0.7 + random() * 0.6);
  const surgePhases = Array.from({ length: SURGES }, () => random() * FULL_TURN);
  let least = Number.POSITIVE_INFINITY;
  let most = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < ROUGH_SAMPLES; i++) {
    const value = roughness(roughAmplitudes, roughPhases, (i / ROUGH_SAMPLES) * FULL_TURN);
    least = Math.min(least, value);
    most = Math.max(most, value);
  }
  const margin = ROUGH_MARGIN * (most - least);
  return {
    driftAngle,
    lobeCount,
    lobePhase,
    roughAmplitudes,
    roughPhases,
    roughMin: least - margin,
    roughSpan: most - least + 2 * margin,
    progress: progressTable(rates, surgePhases),
  };
}

/** How far along its travel a front is at life fraction `u`, from 0 to 1. */
export function gustProgress(draw: GustDraw, u: number): number {
  const at = Math.min(Math.max(u, 0), 1) * PROGRESS_STEPS;
  const step = Math.min(Math.floor(at), PROGRESS_STEPS - 1);
  const share = at - step;
  return draw.progress[step]! * (1 - share) + draw.progress[step + 1]! * share;
}

/** How much of the edge shows at life fraction `u`, from 0 to 1. None before the impact. */
export function gustGrowth(u: number): number {
  const tau = Math.min(Math.max(u, 0), 1) * GUST.sim;
  return tau <= GUST.impact ? 0 : 1 - Math.exp(-(tau - GUST.impact) / GUST.edgeGrowth);
}

/** The edge's extra reach toward `theta` at full growth, as a fraction of the mean radius:
    the lobes plus the roughness, from 0 to just under `GUST.lobes + GUST.roughness`. */
export function gustEdge(draw: GustDraw, theta: number): number {
  const lobe = 0.5 * (1 + Math.sin(draw.lobeCount * theta + draw.lobePhase));
  const rough =
    (roughness(draw.roughAmplitudes, draw.roughPhases, theta) - draw.roughMin) / draw.roughSpan;
  return GUST.lobes * lobe + GUST.roughness * rough;
}

/** Fills `out` with a wave's front at life fraction `u`, measured from the origin
    (`originX`, `originY`), for a front that ends at mean radius `full`. */
export function gustFrontInto(
  out: GustFront,
  draw: GustDraw,
  full: number,
  u: number,
  originX: number,
  originY: number,
): void {
  const radius = gustProgress(draw, u) * full;
  out.x = originX + GUST.drift * radius * Math.cos(draw.driftAngle);
  out.y = originY + GUST.drift * radius * Math.sin(draw.driftAngle);
  out.radius = radius;
  out.thickness = rippleBandThickness(radius);
  out.growth = gustGrowth(u);
}

/** How far the front reaches from its center toward `theta`. */
export function gustReach(wave: GustWave, theta: number): number {
  return wave.radius * (1 + wave.growth * gustEdge(wave.draw, theta));
}

/** A point's offset from the front, in band thicknesses. Below 0 means the front has reached
    it. Capped at ±`GUST_Z_LIMIT`. */
export function gustOffset(wave: GustWave, x: number, y: number): number {
  const dx = x - wave.x;
  const dy = y - wave.y;
  const distance = Math.hypot(dx, dy);
  const limit = GUST_Z_LIMIT * wave.thickness;
  // Well inside the mean radius, or well past the edge's farthest reach, the edge can't change
  // the answer, so skip the trig.
  if (distance <= wave.radius - limit) return -GUST_Z_LIMIT;
  if (distance >= wave.radius * (1 + wave.growth * EDGE_LIMIT) + limit) return GUST_Z_LIMIT;
  const z = (distance - gustReach(wave, Math.atan2(dy, dx))) / wave.thickness;
  return Math.min(GUST_Z_LIMIT, Math.max(-GUST_Z_LIMIT, z));
}

/** Whether the front has reached a box, judged by the box's point nearest the front's center.
    The edge varies by at most 11% of the radius, so that point is a fair stand-in. */
export function gustReachesBox(wave: GustWave, box: Readonly<Box>): boolean {
  const x = Math.min(Math.max(wave.x, box.left), box.right);
  const y = Math.min(Math.max(wave.y, box.top), box.bottom);
  return gustOffset(wave, x, y) <= 0;
}

/** The mean radius where a front ends: when its life is over, every exit point (measured from
    the origin) is at least `RIPPLE_BAND_TRAIL` band thicknesses behind it. The edge only adds
    reach, so the mean radius is enough. */
export function gustFullRadius(driftAngle: number, exitPoints: readonly GustPoint[]): number {
  const dx = GUST.drift * Math.cos(driftAngle);
  const dy = GUST.drift * Math.sin(driftAngle);
  // R = max |q − c(R)| + trail · thickness(R). The right side changes by at most
  // drift + trail · thickGrow (under 0.5) per pixel of R, so iterating converges.
  let radius = 0;
  for (let i = 0; i < 64; i++) {
    let far = 0;
    for (const q of exitPoints)
      far = Math.max(far, Math.hypot(q.x - dx * radius, q.y - dy * radius));
    radius = far + RIPPLE_BAND_TRAIL * rippleBandThickness(radius);
  }
  return radius;
}
